// Flight line that automatically wraps over multiple lines.

import React from 'react';
import style from './flightPlanTrack.module.scss';
import classNames from 'classnames';
import {
    AirportNode,
    BlankDurationNode,
    DurationNode,
    EmptyDurationNode,
    Node
} from 'acscp/components/legs/flightPlan/flightPlanNodes';
import { getArrivalLocation, getDepartureLocation, Leg, locationMatch } from 'acscp/store';

export interface SingleLineFlightPlanTrackProps {
    legs: Leg[];
    withDurations: boolean;
    withBackground?: boolean;
}

export class SingleLineFlightPlanTrack extends React.Component<SingleLineFlightPlanTrackProps> {
    durationRefs: HTMLDivElement[] = [];
    trackContainer: HTMLDivElement;

    // Container padding used for correct layout of clusters.
    // Helps to wrap durations that would be cut off the right edge of the container.
    containerLayoutRightPadding: string;

    componentDidMount(): void {
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    }

    componentDidUpdate(): void {
        this.handleResize();
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = (): void => {
        if (this.durationRefs.length === 0 || this.trackContainer === null) {
            return;
        }

        // Reset element sizes and container padding
        this.durationRefs.forEach(durationNode => durationNode.style.width = 'auto');
        this.trackContainer.style.paddingRight = this.containerLayoutRightPadding;

        // This has a desirable side effect: forces flex to reflow
        const trackContainerWidth = this.trackContainer.offsetWidth;

        // We want to stretch duration nodes that are last on the line,
        // so they would continue till the end of the container.
        // Since all nodes within the container have fixed length,
        // all of the last duration nodes will have the same offsetLeft value.
        const maxPosition = this.durationRefs
            .map(node => node.offsetLeft)
            .reduce((max, current) => Math.max(max, current));
        const durationsToStretch = this.durationRefs
            .filter(node => Math.abs(node.offsetLeft - maxPosition) < 2);

        const requiredStretch = trackContainerWidth - maxPosition - 1;

        const widthToSet = requiredStretch + 'px';
        durationsToStretch.forEach(durationNode => durationNode.style.width = widthToSet);

        // Remove the padding to allow the stretched durations stay on their original line.
        this.trackContainer.style.paddingRight = '0px';
    }

    render(): JSX.Element {
        const { legs, withBackground, withDurations } = this.props;

        const nodes = this.parseLegsIntoNodes(legs);
        const clusteredNodes = this.parseNodesIntoClusters(nodes);

        return(
            <div className={classNames({[style.NoDurations]: !withDurations})}>
                {this.renderClusters(withBackground, clusteredNodes, this.registerTrackContainer)}
            </div>
        );
    }

    registerTrackContainer = (trackContainer: HTMLDivElement | null): void => {
        if (trackContainer) {
            this.trackContainer = trackContainer;

            const rightPadding = window.getComputedStyle(this.trackContainer).getPropertyValue('padding-right');
            const rightPaddingValue = parseInt(rightPadding.replace('px', ''), 10);

            // Only update the helper padding value, when there is an actual value (these are configured through CSS).
            // We discard undefined values caused by unrendered content
            // and 0px values set by this script in previous renders.
            if (!isNaN(rightPaddingValue) && rightPaddingValue > 0) {
                this.containerLayoutRightPadding = rightPadding;
            }
        }
    }

    register = (durationNode: HTMLDivElement | null): void => {
        if (durationNode) {
            this.durationRefs.push(durationNode);
        }
    }

    parseLegsIntoNodes(legs: Leg[]): Node[] {
        const nodes = new Array<Node>();

        for (let i = 0; i < legs.length; i++) {
            const departure = new AirportNode(legs[i].departureLocation, legs[i].departureAirportId);

            // Add departure only for the first leg OR if the previous leg arrives at a different location
            if (i === 0) {
                nodes.push(new EmptyDurationNode(this.register));
                nodes.push(departure);
            } else {
                const previousArrival = getArrivalLocation(legs[i - 1]);

                if (!locationMatch(previousArrival, departure)) {
                    nodes.push(departure);
                }
            }

            // Add flight duration and an empty helper node
            nodes.push(new DurationNode(legs[i].flightTimeDuration, this.register));
            nodes.push(new BlankDurationNode(this.register));

            const arrival = new AirportNode(legs[i].arrivalLocation, legs[i].arrivalAirportId);

            if (i === legs.length - 1) {
                // Simply add the arrival node for the last leg
                nodes.push(arrival);
                nodes.push(new EmptyDurationNode(this.register));
            } else {
                const nextDeparture = getDepartureLocation(legs[i + 1]);

                if (locationMatch(arrival, nextDeparture)) {
                    // Mark arrival as "hop" if the next leg departs from the same location
                    // "Hop" nodes are styled differently
                    nodes.push(arrival.markAsHop());
                } else {
                    // Separate non-matching arrival and departure locations with helper nodes
                    nodes.push(arrival);
                    nodes.push(new EmptyDurationNode(this.register));
                    nodes.push(new EmptyDurationNode(this.register));
                }
            }
        }
        return nodes;
    }

    parseNodesIntoClusters(nodes: Node[]): Node[][] {
        const clusters: Node[][] = [];
        let clusterKey = 0;
        let thisCluster: Node[];
        for (let i = 0; i < nodes.length; i += 2, clusterKey++) {
            thisCluster = [];
            thisCluster.push(nodes[i]);
            thisCluster.push(nodes[i + 1]);
            // Clusters can be duration-airport, duration-airport-duration or airport-duration
            // As such we always take two nodes, and on this condition only, take a third
            if (nodes[i] instanceof DurationNode && i < nodes.length - 2) {
                thisCluster.push(nodes[i + 2]);
                i++;
            }
            clusters.push(thisCluster);
        }
        return clusters;
    }

    renderClusters(
        withBackground: boolean | undefined,
        clusters: Node[][],
        registerTrackContainer: (ref: HTMLDivElement | null
    ) => void): JSX.Element {
        return (
            <div
                className={classNames(
                    style.TrackContainer,
                    {
                        [style.WithBackground]: withBackground
                    }
                )}
                ref={div => registerTrackContainer(div)}
            >
                {clusters.map((cluster: Node[], index: number) => this.renderCluster(cluster, index))}
            </div>
        );
    }

    renderCluster(cluster: Node[], clusterKey: number): JSX.Element {
        return (
            <div key={clusterKey} className={style.Cluster}>
                {cluster.map((node: Node, index: number) => node.render(index))}
            </div>
        );
    }
}
